import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import Axios from 'axios';

import { Box, Typography } from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import { useConfig } from 'src/hooks/useConfig';
import { appendContactSupport, axiosHeaders, getLocalisedErrorString } from 'src/utils/helpers';

import Cards from 'src/plan-n-billing/components/Cards/Cards';
import Invoices from 'src/plan-n-billing/components/Invoices/Invoices';
import { CustomError } from 'src/types';
import EmptyText from 'src/shared/components/EmptyText/EmptyText';
import LoadingScreen from 'src/shared/screens/LoadingScreen';
import SubCompanyAccessModal from 'src/plan-n-billing/components/SubCompanyAccessModal';

import styles from './style';
import SubscriptionContactUs from 'src/subscription/components/SubscriptionContactUs';
import PurchaseCredits from 'src/plan-n-billing-new/PurchaseCredits';

const PlanAndBillingNew = () => {
  const { user } = useAuth();
  const { ready, t } = useTranslation();
  const { API } = useConfig();
  const [loading, setLoading] = useState(false);
  const [billingPlans, setBillingPlans] = useState<Record<string, any>>({});
  const [subsError, setSubsError] = useState('');

  const formatEuroAmount = (amount: number): string => {
    return amount.toFixed(2).replace('.', ',');
  };

  const getBillingPlans = async () => {
    setLoading(true);
    try {
      const companyId = user?.companyID || user?.company;
      if (!companyId) {
        throw new Error('Company ID not found');
      }

      const response = await Axios.get(
        `${API.getBillingPlans}/${companyId}`,
        axiosHeaders(localStorage.getItem('PROCYS_accessToken'))
      );

      if (response.data.success && response.data.data) {
        const billingData = response.data.data;

        setBillingPlans({
          ...billingData,
          currentPlan: billingData.plans?.[0] || null,
          planCount: billingData.plans?.length || 0,
        });
      } else {
        setSubsError(appendContactSupport(window.config.support_email, t('SUBSCRIPTION_RETRIEVE_DETAILS_FAILURE'), t));
      }
      setLoading(false);
    } catch (e) {
      const err = e as CustomError;
      setLoading(false);
      setSubsError(appendContactSupport(
        window.config.support_email,
        getLocalisedErrorString(err?.response?.data?.i18n || 'SUBSCRIPTION_RETRIEVE_DETAILS_FAILURE', t),
        t
      ));
    }
  };

  useEffect(() => {
    getBillingPlans();
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  if ((user?.isSubCompany || user?.isPartnerSubCompany) && user.parent) {
    return <SubCompanyAccessModal open parentName={user.parentName} />;
  }

  if (subsError) {
    return <EmptyText str={subsError} />;
  }

  return (
    <Box sx={styles.mainContainer}>
      <Box sx={styles.container}>
        <Typography style={styles.descTitle}>
          {ready && t('BILLING_PLAN_CREDITS_TITLE')}
          {billingPlans.billingType && (
            <Typography component="span" sx={{ fontSize: '0.8em', opacity: 0.7, marginLeft: 1 }}>
              ({billingPlans.billingType === 'enterprise' ? 'Enterprise' : 'Pay As You Go'})
            </Typography>
          )}
        </Typography>

        {billingPlans.currentPlan && (
          <Box sx={{ marginBottom: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {billingPlans.currentPlan.name}
            </Typography>
            {billingPlans.currentPlan.duration && (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Duration: {billingPlans.currentPlan.duration} days
              </Typography>
            )}
          </Box>
        )}

        <Box sx={styles.creditsContainer}>
          {billingPlans.currentPlan && (
            <>
              <Box sx={styles.creditsInfoContainer}>
                <Typography variant="subtitle2" sx={styles.creditInfoTextTitle} gutterBottom>
                  {t('BILLING_PLAN_CREDITS')}
                </Typography>
                <Typography variant="h4" sx={styles.creditInfoTextValue} fontWeight="bold">
                  {billingPlans.currentPlan.credits || 0}
                </Typography>
              </Box>

              <Box sx={styles.creditsInfoContainer}>
                <Typography variant='subtitle2' sx={styles.creditInfoTextTitle} gutterBottom>
                  {t('BILLING_PLAN_AMOUNT')}
                </Typography>
                <Typography variant="h4" sx={styles.creditInfoTextValue} fontWeight="bold">
                  €{formatEuroAmount((billingPlans.currentPlan.amount || 0) / 100)}
                </Typography>
              </Box>

              {billingPlans.billingType === 'enterprise' && billingPlans.currentPlan.virtualCredits && (
                <Box sx={styles.creditsInfoContainer}>
                  <Typography variant='subtitle2' sx={styles.creditInfoTextTitle} gutterBottom>
                    {t('BILLING_PLAN_VIRTUAL_CREDITS')}
                  </Typography>
                  <Typography variant="h4" sx={styles.creditInfoTextValue} fontWeight="bold">
                    {billingPlans.currentPlan.virtualCredits}
                  </Typography>
                </Box>
              )}

              {billingPlans.currentPlan.freeCredits && (
                <Box sx={styles.creditsInfoContainer}>
                  <Typography variant='subtitle2' sx={styles.creditInfoTextTitle} gutterBottom>
                    {t('BILLING_PLAN_FREE_CREDITS')}
                  </Typography>
                  <Typography variant="h4" sx={styles.creditInfoTextValue} fontWeight="bold">
                    {billingPlans.currentPlan.freeCredits}
                  </Typography>
                </Box>
              )}

              {billingPlans.currentPlan.users && (
                <Box sx={styles.creditsInfoContainer}>
                  <Typography variant='subtitle2' sx={styles.creditInfoTextTitle} gutterBottom>
                    {t('BILLING_PLAN_USERS')}
                  </Typography>
                  <Typography variant="h4" sx={styles.creditInfoTextValue} fontWeight="bold">
                    {billingPlans.currentPlan.users}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>

        <Box sx={styles.creditsActionButtonsContainer}>
          <SubscriptionContactUs user={user} />
          <PurchaseCredits billingPlan={billingPlans.currentPlan} billingType={billingPlans.billingType} />
        </Box>
      </Box>

      <Cards subscription={billingPlans} />
      <Invoices user={user} />
    </Box>
  )
}

export default PlanAndBillingNew
